import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useDocuments } from './DocumentContext';
import { useAuth } from './AuthContext';

export interface SupportChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  isStreaming?: boolean;
  attachedFiles?: Array<{
    id: string;
    name: string;
    type: string;
  }>;
  context?: ChatContext;
}

export interface ChatContext {
  type: 'document' | 'report' | 'workflow' | 'comparison';
  id: string;
  name: string;
  preview?: string;
  metadata?: Record<string, any>;
}

interface ChatSupportContextType {
  isOpen: boolean;
  messages: SupportChatMessage[];
  attachedContext: ChatContext | null;
  availableContext: ChatContext | null;
  unreadCount: number;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  /** Opens the bubble and pre-fills the input with `message` — does NOT auto-send. */
  openChatWithDraft: (message: string) => void;
  draftMessage: string | null;
  consumeDraftMessage: () => void;
  sendMessage: (message: string, files?: File[], documentIds?: string[]) => Promise<void>;
  attachCurrentContext: () => void;
  removeAttachedContext: () => void;
  clearUnreadCount: () => void;
  clearChat: () => void;
}

const ChatSupportContext = createContext<ChatSupportContextType | undefined>(undefined);

export const useChatSupport = () => {
  const context = useContext(ChatSupportContext);
  if (!context) {
    throw new Error('useChatSupport must be used within ChatSupportProvider');
  }
  return context;
};

interface ChatSupportProviderProps {
  children: ReactNode;
}

export const ChatSupportProvider: React.FC<ChatSupportProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<SupportChatMessage[]>([]);
  const [attachedContext, setAttachedContext] = useState<ChatContext | null>(null);
  const [availableContext, setAvailableContext] = useState<ChatContext | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [ephemeralDocumentIds, setEphemeralDocumentIds] = useState<string[]>([]);
  const [draftMessage, setDraftMessage] = useState<string | null>(null);

  const location = useLocation();
  const { selectedDocuments } = useDocuments();
  const { user } = useAuth();

  // Load messages from localStorage on mount - user-specific
  useEffect(() => {
    if (!user?.id) {
      // Clear messages if no user is logged in
      setMessages([]);
      return;
    }

    const userSpecificKey = `supportChatMessages_${user.id}`;
    const savedMessages = localStorage.getItem(userSpecificKey);
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        setMessages(parsed.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        })));
      } catch (e) {
        console.error('Failed to load chat messages:', e);
      }
    } else {
      // Clear messages for new user
      setMessages([]);
    }
  }, [user?.id]);

  // Save messages to localStorage whenever they change - user-specific
  useEffect(() => {
    if (!user?.id) return;

    const userSpecificKey = `supportChatMessages_${user.id}`;
    if (messages.length > 0) {
      localStorage.setItem(userSpecificKey, JSON.stringify(messages));
    } else {
      // Clear storage when messages are empty
      localStorage.removeItem(userSpecificKey);
    }
  }, [messages, user?.id]);

  // Detect available context based on current route and state
  useEffect(() => {
    let context: ChatContext | null = null;

    console.log('Context detection:', {
      pathname: location.pathname,
      documentsCount: selectedDocuments?.length || 0,
      documents: selectedDocuments?.map(d => ({ id: d.id, name: d.name })) || []
    });

    // Check for workflow report
    if (location.pathname.match(/\/workflow-report/)) {
      const reportData = location.state as any;
      const reportId = reportData?.reportId || 'current-report';
      const reportTitle = reportData?.reportTitle || 'Current Workflow Report';
      const workflowType = reportData?.workflowType || 'Unknown';
      const customerName = reportData?.customerName;
      const reportContent = reportData?.content || '';

      // Convert reportContent to string if it's an object (like UniversalJsonResponse)
      const reportContentString = typeof reportContent === 'string'
        ? reportContent
        : typeof reportContent === 'object'
          ? JSON.stringify(reportContent)
          : String(reportContent);

      context = {
        type: 'report',
        id: reportId,
        name: reportTitle,
        preview: `${workflowType} workflow report${customerName ? ` for ${customerName}` : ''}`,
        metadata: {
          workflowType,
          reportId,
          customerName,
          documentsCount: reportData?.documents?.length || 0,
          documentNames: reportData?.documents?.map((d: any) => d.name) || [],
          reportContent: reportContentString ? reportContentString.substring(0, 8000) : '', // Include report content (truncated)
          isUniversalJson: reportData?.isUniversalJson || false,
          universalJsonResponse: reportData?.universalJsonResponse
        }
      };
    }
    // Check for comparison page
    else if (location.pathname.includes('/comparison')) {
      if (selectedDocuments && selectedDocuments.length > 0) {
        context = {
          type: 'comparison',
          id: 'current-comparison',
          name: `Comparing ${selectedDocuments.length} documents`,
          preview: selectedDocuments.map(d => d.name).join(', '),
          metadata: {
            documentIds: selectedDocuments.map(d => d.id),
            documentNames: selectedDocuments.map(d => d.name)
          }
        };
      } else {
        // Even without docs, show comparison context
        context = {
          type: 'comparison',
          id: 'comparison-page',
          name: 'Document Comparison Page',
          preview: 'Currently on the document comparison page'
        };
      }
    }
    // Check for home page with documents
    else if (location.pathname === '/' && selectedDocuments && selectedDocuments.length > 0) {
      if (selectedDocuments.length === 1) {
        const doc = selectedDocuments[0];
        context = {
          type: 'document',
          id: doc.id,
          name: doc.name,
          preview: `Document: ${doc.name}`
        };
      } else {
        context = {
          type: 'document',
          id: 'multiple-docs',
          name: `${selectedDocuments.length} Selected Documents`,
          preview: selectedDocuments.map(d => d.name).join(', '),
          metadata: {
            documentIds: selectedDocuments.map(d => d.id),
            documentNames: selectedDocuments.map(d => d.name)
          }
        };
      }
    }
    // Check for workflow in progress
    else if (location.pathname.includes('/workflow/')) {
      const workflowType = location.pathname.split('/')[2];
      context = {
        type: 'workflow',
        id: workflowType,
        name: `${workflowType} Workflow`,
        preview: `Active workflow: ${workflowType}`
      };
    }
    // Default context for any page
    else {
      const pageName = location.pathname === '/' ? 'Home' :
                      location.pathname.replace('/', '').replace('-', ' ').split('/')[0];
      context = {
        type: 'workflow',
        id: 'current-page',
        name: `Current Page: ${pageName}`,
        preview: `Currently viewing: ${location.pathname}`
      };
    }

    console.log('Detected context:', context);
    setAvailableContext(context);
  }, [location, selectedDocuments]);

  // Track unread messages when chat is closed
  useEffect(() => {
    if (!isOpen && messages.length > 0) {
      // Find the last user message using reverse iteration
      let lastUserMessageIndex = -1;
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].sender === 'user') {
          lastUserMessageIndex = i;
          break;
        }
      }
      const unreadAiMessages = messages.slice(lastUserMessageIndex + 1).filter(m => m.sender === 'ai');
      setUnreadCount(unreadAiMessages.length);
    }
  }, [isOpen, messages]);

  const toggleChat = useCallback(() => {
    setIsOpen(prev => !prev);
    if (!isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  const openChat = useCallback(() => {
    setIsOpen(true);
    setUnreadCount(0);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openChatWithDraft = useCallback((message: string) => {
    setDraftMessage(message);
    setIsOpen(true);
    setUnreadCount(0);
  }, []);

  const consumeDraftMessage = useCallback(() => {
    setDraftMessage(null);
  }, []);

  const clearChat = useCallback(async () => {
    // Clear ephemeral documents from server
    if (ephemeralDocumentIds.length > 0) {
      try {
        const token = localStorage.getItem('riskninja-token');
        if (token) {
          const apiUrl = process.env.REACT_APP_API_URL || '';
          await fetch(`${apiUrl}/api/chat/support/clear-ephemeral`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ documentIds: ephemeralDocumentIds })
          });
        }
      } catch (error) {
        console.error('Failed to clear ephemeral documents:', error);
      }
    }

    // Clear local state
    setMessages([]);
    setAttachedContext(null);
    setEphemeralDocumentIds([]);
    // Remove user-specific chat messages
    if (user?.id) {
      const userSpecificKey = `supportChatMessages_${user.id}`;
      localStorage.removeItem(userSpecificKey);
    }
  }, [ephemeralDocumentIds, user?.id]);

  const attachCurrentContext = useCallback(() => {
    if (availableContext) {
      setAttachedContext(availableContext);
    }
  }, [availableContext]);

  const removeAttachedContext = useCallback(() => {
    setAttachedContext(null);
  }, []);

  const clearUnreadCount = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const sendMessage = useCallback(async (message: string, files?: File[], documentIds?: string[]) => {
    // Create user message and add it immediately
    const userMessage: SupportChatMessage = {
      id: `msg-${Date.now()}`,
      content: message,
      sender: 'user',
      timestamp: new Date(),
      context: attachedContext || undefined
    };

    // Add user message immediately for instant feedback
    setMessages(prev => [...prev, userMessage]);

    // Handle file uploads or pre-uploaded document IDs
    let uploadedFiles: Array<{ id: string; name: string; type: string }> = [];

    // If documentIds are provided, use those (files were already uploaded)
    if (documentIds && documentIds.length > 0) {
      uploadedFiles = documentIds.map(id => ({
        id,
        name: 'Uploaded Document', // We don't have the name here, but it's already processed
        type: 'application/octet-stream' // Generic type since we don't know
      }));

      // Update the message with attached files
      setMessages(prev => prev.map(msg =>
        msg.id === userMessage.id
          ? { ...msg, attachedFiles: uploadedFiles }
          : msg
      ));
    }
    // Otherwise, handle file uploads if present (legacy behavior)
    else if (files && files.length > 0) {
      try {
        const token = localStorage.getItem('riskninja-token');
        if (!token) {
          throw new Error('Authentication required');
        }

        const apiUrl = process.env.REACT_APP_API_URL || '';

        // Upload files as ephemeral documents for chat
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);

          const response = await fetch(`${apiUrl}/api/chat/support/upload-ephemeral`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });

          if (response.ok) {
            const result = await response.json();
            uploadedFiles.push({
              id: result.id,
              name: result.name || file.name,
              type: result.type || file.type
            });
            // Track ephemeral document IDs for cleanup
            setEphemeralDocumentIds(prev => [...prev, result.id]);
          } else {
            console.error('Failed to upload file:', file.name, response.status);
          }
        }

        if (uploadedFiles.length > 0) {
          // Update the already-added message with attached files
          setMessages(prev => prev.map(msg =>
            msg.id === userMessage.id
              ? { ...msg, attachedFiles: uploadedFiles }
              : msg
          ));
        }
      } catch (error) {
        console.error('Failed to upload files:', error);
      }
    }

    // Create AI response message
    const aiMessage: SupportChatMessage = {
      id: `msg-${Date.now() + 1}`,
      content: '',
      sender: 'ai',
      timestamp: new Date(),
      isStreaming: true
    };

    setMessages(prev => [...prev, aiMessage]);

    try {
      const token = localStorage.getItem('riskninja-token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const apiUrl = process.env.REACT_APP_API_URL || '';

      const requestBody = {
        message,
        ...(attachedContext && { context: attachedContext }),
        ...(uploadedFiles.length > 0 && { documentIds: uploadedFiles.map(f => f.id) }),
        conversationHistory: messages.slice(-10).map(m => ({
          role: m.sender === 'user' ? 'user' : 'assistant',
          content: m.content
        })).filter(m => m.content.trim()) // Filter out empty messages
      };

      console.log('📤 Support chat request body:', {
        ...requestBody,
        uploadedFiles: uploadedFiles.length,
        documentIds: uploadedFiles.map(f => f.id)
      });

      const response = await fetch(`${apiUrl}/api/chat/support/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let buffer = ''; // Buffer for incomplete chunks

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            // Process any remaining data in buffer
            if (buffer.trim()) {
              if (buffer.startsWith('data: ')) {
                try {
                  const data = JSON.parse(buffer.slice(6));
                  if (data.content) {
                    fullContent += data.content;
                    setMessages(prev => prev.map(msg =>
                      msg.id === aiMessage.id
                        ? { ...msg, content: fullContent, isStreaming: true }
                        : msg
                    ));
                  }
                } catch (e) {
                  // Ignore parse errors for final buffer
                }
              }
            }
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk; // Add to buffer
          const lines = buffer.split('\n');

          // Keep the last incomplete line in buffer
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.content) {
                  fullContent += data.content;
                  setMessages(prev => prev.map(msg =>
                    msg.id === aiMessage.id
                      ? { ...msg, content: fullContent, isStreaming: true }
                      : msg
                  ));
                }
              } catch (e) {
                // Ignore parse errors
              }
            }
          }
        }
      }

      // Mark streaming as complete
      setMessages(prev => prev.map(msg =>
        msg.id === aiMessage.id
          ? { ...msg, content: fullContent, isStreaming: false }
          : msg
      ));

      // Clear attached context after sending (but keep ephemeral docs for session)
      setAttachedContext(null);

    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => prev.map(msg =>
        msg.id === aiMessage.id
          ? { ...msg, content: 'Sorry, I encountered an error. Please try again.', isStreaming: false }
          : msg
      ));
    }
  }, [attachedContext, messages]);

  const value: ChatSupportContextType = {
    isOpen,
    messages,
    attachedContext,
    availableContext,
    unreadCount,
    toggleChat,
    openChat,
    closeChat,
    openChatWithDraft,
    draftMessage,
    consumeDraftMessage,
    sendMessage,
    attachCurrentContext,
    removeAttachedContext,
    clearUnreadCount,
    clearChat
  };

  return (
    <ChatSupportContext.Provider value={value}>
      {children}
    </ChatSupportContext.Provider>
  );
};

export default ChatSupportContext;
